import { addMinutes, isValid, parseISO, subMinutes } from 'date-fns';
import type { RelationshipEvidence } from '../types/relationships';

interface NearbyLogFilters {
  fqdnSearch: [string];
  dateRange: { from: Date; to: Date };
}

export type RelationshipEvidenceLogLink =
  | { kind: 'command'; filters: NearbyLogFilters; label: 'Open nearby commands' }
  | { kind: 'chat'; filters: NearbyLogFilters; label: 'Open nearby chat' };

const FQDN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Open a bounded search on the evidence's server, never an invented exact jump.
 * Normalization can truncate source timestamp precision or substitute receipt
 * time, and displayed player names can be newer aliases than the source record.
 */
export function getRelationshipEvidenceLogLink(
  evidence: RelationshipEvidence,
): RelationshipEvidenceLogLink | null {
  const { source, server, at } = evidence;
  if (!source || !['es:cmd_logs_v1', 'es:chat_messages'].includes(source.index)) return null;
  if (typeof source.id !== 'string' || !source.id.trim() || source.id.length > 512 || /[\u0000-\u001f\u007f]/.test(source.id)) return null;
  if (typeof server !== 'string' || server !== server.trim() || !FQDN_PATTERN.test(server)) return null;
  if (typeof at !== 'string' || at !== at.trim() || !TIMESTAMP_PATTERN.test(at)) return null;

  const timestamp = parseISO(at);
  if (!isValid(timestamp)) return null;
  const filters: NearbyLogFilters = {
    fqdnSearch: [server],
    dateRange: { from: subMinutes(timestamp, 5), to: addMinutes(timestamp, 5) },
  };
  return source.index === 'es:cmd_logs_v1'
    ? { kind: 'command', filters, label: 'Open nearby commands' }
    : { kind: 'chat', filters, label: 'Open nearby chat' };
}
